// Per-individual view of the family result (pure). Assigns a role (precedence-
// ordered), tags, the patterns the individual supports, and any individual
// actions (e.g. a cascade recommendation). Keyed by pid.

import { ROLE, CARRIER_STATUS } from './constants';

// The single carrier role/tag for an individual who carries different bases
// across diseases. Precedence: obligate > tested > reported > generic carrier.
// obligate is kept ABOVE tested to preserve the pre-flag behaviour for a person
// who is a tested carrier for one disease and an obligate carrier for another
// (both reachable with the feature flag OFF). reported and the generic carrier
// are flag-on-only additions below them. NB this per-person headline precedence
// intentionally differs from the per-disease basis precedence in
// deriveCarrierStatus (tested > reported > obligate), which ranks evidence
// strength within a single condition. Returns null when not a carrier.
function carrierKind(ind) {
  const states = Object.values(ind.carrierByDisease || {});
  if (states.includes(CARRIER_STATUS.obligateCarrier))
    return { role: ROLE.obligateCarrier, tag: 'obligate carrier' };
  if (states.includes(CARRIER_STATUS.testedCarrier))
    return { role: ROLE.testedCarrier, tag: 'tested carrier' };
  if (states.includes(CARRIER_STATUS.reportedCarrier))
    return { role: ROLE.reportedCarrier, tag: 'reported carrier' };
  if (states.includes(CARRIER_STATUS.carrier))
    return { role: ROLE.carrier, tag: 'carrier' };
  return null;
}

// Precedence: proband > affected > carrier > cascade candidate > unaffected.
function assignRole(ind, cascadeByPid) {
  if (ind.isProband) return ROLE.proband;
  if (ind.affected) return ROLE.affected;
  const carrier = carrierKind(ind);
  if (carrier) return carrier.role;
  if (cascadeByPid.has(ind.pid)) return ROLE.cascadeCandidate;
  return ROLE.unaffected;
}

// Tags are attribute-based (independent of the single `role`), so a proband who
// is also a carrier surfaces both facts.
function buildTags(ind, role) {
  const tags = [];
  if (ind.isProband) tags.push('proband');
  if (ind.affected) tags.push('affected');
  const carrier = carrierKind(ind);
  if (carrier) tags.push(carrier.tag);
  if (role === ROLE.cascadeCandidate) tags.push('cascade candidate');
  return tags;
}

export function buildIndividualSections(ped, family) {
  const cascadeByPid = new Map(
    (family.cascadeCandidates || []).map((c) => [c.pid, c])
  );
  const sections = {};
  for (const ind of ped.individuals) {
    if (ind.isDummy) continue;
    const role = assignRole(ind, cascadeByPid);
    const referencedPatterns = (family.inheritancePatterns || [])
      .filter((p) => (p.supportingIds || []).includes(ind.pid))
      .map((p) => p.id);
    sections[ind.pid] = {
      role,
      tags: buildTags(ind, role),
      referencedPatterns,
      // Uppercased gene symbols this individual carries a pathogenic variant in
      // — consumed by Phenomizer Phase C molecular support.
      pathogenicGenes: (ind.genotypes || [])
        .filter((g) => g.interpretation === 'pathogenic' && g.gene)
        .map((g) => g.gene.toUpperCase()),
      individualActions: cascadeByPid.has(ind.pid)
        ? [cascadeByPid.get(ind.pid)]
        : []
    };
  }
  return sections;
}
